import React, { useState, useMemo, useCallback, useEffect } from 'react';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Box from '@mui/material/Box';
import clsx from 'clsx';
import './discount-modal.scss';
import { DiscountType, OrderDiscountLevel } from '../../../constants';
import {
  getCurrencySign,
  getFormattedAmount,
  toMajorCurrency,
  toMinorCurrency,
} from '@zonofi/common/functions/format-amount';
import { OrderDataLine, OrderDataSummary } from '@zonofi/zono-money-store';
import { ItemLevelSummary } from './item-level-summary';
import { OrderLedgerSummary } from './order-ledger-summary';

interface AddOrderDiscountModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (
    line: OrderDataLine,
    discountType: DiscountType,
    discount: number,
    orderDiscountType: OrderDiscountLevel
  ) => void;
  data: OrderDataLine;
  orderSummary?: OrderDataSummary;
  orderDiscountType: OrderDiscountLevel;
  orderLevelDiscount?: {
    discount: number;
    discountType: string;
  };
  isOrderSummary?: boolean;
}

const Strings = {
  CANCEL: 'Cancel',
  APPLY: 'Apply',
  OLD_PRICE: 'Old Price',
  NEW_PRICE: 'New Price',
  QUANTITY: 'Quantity',
  INLINE_PRICE: 'Inline Price',
  ESTIMATE: 'Estimate',
  DISCOUNT_INFO_LABEL_PERCENT: 'Enter discount percent',
  DISCOUNT_INFO_LABEL_VALUE: 'Enter discount value',
  PERCENT: '%',
  ENTER_VALUE: 'Enter value',
  PERCENT_DISCOUNT_MAX: 'Max 100%',
  DISCOUNT_VALUE_MAX: 'Max discount is X',
  ADD_ITEM_LEVEL_DISC: 'Add Item Level Discount',
  ADD_ORDER_LEVEL_DISC: 'Add Order Level Discount',
  INVALID_QUANTITY: 'Invalid quantity',
  INVALID_PRICE: 'Invalid price',
};

export function DiscountModal({
  open,
  onClose,
  onApply,
  data,
  orderSummary,
  orderDiscountType,
  orderLevelDiscount,
  isOrderSummary = false,
}: AddOrderDiscountModalProps) {
  const [discountType, setDiscountType] = useState<DiscountType>('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [error, setError] = useState('');

  // Computed values with proper validation
  const itemTotalPrice = useMemo(() => {
    return (data?.price || 0) * (data?.newQty || 0);
  }, [data]);

  const orderTotalPrice = useMemo(() => {
    return Number(orderSummary?.total || 0);
  }, [orderSummary]);

  const maxDiscountValue = useMemo(() => {
    return orderDiscountType === 'item' ? itemTotalPrice : orderTotalPrice;
  }, [orderDiscountType, itemTotalPrice, orderTotalPrice]);

  // Calculate discount amount in minor currency
  const discountAmountInMinor = useMemo(() => {
    if (!discountValue || isNaN(Number(discountValue))) return 0;

    const basePrice =
      orderDiscountType === 'item' ? data?.price : orderTotalPrice;

    if (discountType === 'percent') {
      return Math.round((basePrice * Number(discountValue)) / 100);
    } else {
      // Convert user input (major currency) to minor currency
      const directDiscount =
        orderDiscountType === 'item'
          ? Number(discountValue) / data?.newQty
          : Number(discountValue);
      return toMinorCurrency(directDiscount);
    }
  }, [
    discountValue,
    discountType,
    orderDiscountType,
    data?.price,
    orderTotalPrice,
    data?.newQty,
  ]);

  // Calculate per-quantity discount for item-level discounts
  const discountPerQuantity = useMemo(() => {
    if (orderDiscountType !== 'item' || !data?.newQty || data.newQty === 0)
      return 0;
    return discountAmountInMinor;
  }, [discountAmountInMinor, orderDiscountType, data?.newQty]);

  // Calculate new price after discount
  const newPrice = useMemo(() => {
    const basePrice =
      orderDiscountType === 'item' ? data?.price || 0 : orderTotalPrice;

    if (orderDiscountType === 'item') {
      // For item-level: subtract per-unit discount from unit price
      return Math.max(0, basePrice - discountPerQuantity);
    } else {
      // For order-level: subtract total discount from total price
      return Math.max(0, basePrice - discountAmountInMinor);
    }
  }, [
    orderDiscountType,
    data?.price,
    orderTotalPrice,
    discountPerQuantity,
    discountAmountInMinor,
  ]);

  useEffect(() => {
    // Reset whenever switching between item-level ↔ order-level
    setDiscountValue('');
    setDiscountType('percent');
    setError('');
  }, [orderDiscountType, open]);

  // Initialize discount values from existing item-level discount
  useEffect(() => {
    if (data?.lineDiscount?.discount && orderDiscountType === 'item') {
      setDiscountValue(data?.lineDiscount?.discount);
      setDiscountType(data?.lineDiscount?.discountType);
    }
  }, [orderDiscountType, data?.lineDiscount, open]);

  // Initialize discount values from existing order-level discount
  useEffect(() => {
    if (orderLevelDiscount?.discount && orderDiscountType === 'order') {
      const existingDiscountType =
        orderLevelDiscount.discountType as DiscountType;
      setDiscountType(existingDiscountType);

      // Convert to major currency if it's a direct discount
      const displayValue =
        existingDiscountType === 'direct'
          ? toMajorCurrency(orderLevelDiscount.discount)
          : orderLevelDiscount.discount;

      setDiscountValue(String(displayValue));
    }
  }, [orderLevelDiscount, orderDiscountType, open]);

  // Validation effect
  useEffect(() => {
    if (!discountValue || discountValue === '0') {
      setError('');
      return;
    }

    const numValue = Number(discountValue);

    // Check for invalid input
    if (isNaN(numValue) || numValue < 0) {
      setError('Please enter a valid positive number');
      return;
    }

    // Validate based on discount type
    if (discountType === 'percent') {
      if (numValue > 100) {
        setError(Strings.PERCENT_DISCOUNT_MAX);
      } else {
        setError('');
      }
    } else {
      // For direct discount, compare in minor currency
      const discountInMinor = toMinorCurrency(numValue);
      const maxInMajor = toMajorCurrency(maxDiscountValue);

      if (discountInMinor > maxDiscountValue) {
        setError(
          Strings.DISCOUNT_VALUE_MAX.replace('X', maxInMajor.toFixed(2))
        );
      } else {
        setError('');
      }
    }
  }, [discountValue, discountType, maxDiscountValue]);

  const handleClose = useCallback(() => {
    onClose?.();
    setError('');
  }, [onClose]);

  const handleApply = useCallback(() => {
    // Validation
    if (!discountValue || Number(discountValue) <= 0) {
      setError('Please enter a valid discount');
      return;
    }

    if (error) {
      return;
    }

    // Additional validations
    if (orderDiscountType === 'item') {
      if (!data?.newQty || data.newQty <= 0) {
        setError(Strings.INVALID_QUANTITY);
        return;
      }
      if (!data?.price || data.price <= 0) {
        setError(Strings.INVALID_PRICE);
        return;
      }
    }

    const orderline =
      orderDiscountType === 'item' ? data : orderSummary?.lines?.[0] || data;

    if (!orderline) {
      setError('Unable to apply discount');
      return;
    }

    // Calculate final discount in appropriate format
    const finalDiscount =
      discountType === 'percent'
        ? Number(discountValue) // Percent as-is
        : toMinorCurrency(Number(discountValue)); // Convert to minor currency

    onApply(orderline, discountType, finalDiscount, orderDiscountType);
    handleClose();
  }, [
    discountValue,
    error,
    orderDiscountType,
    data,
    orderSummary,
    discountType,
    onApply,
    handleClose,
  ]);

  const handleDiscountTypeChange = useCallback((newType: DiscountType) => {
    setDiscountType(newType);
    setDiscountValue('');
    setError('');
  }, []);

  const handleDiscountValueChange = useCallback((value: string) => {
    // Allow empty string or valid numbers (including decimals)
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      setDiscountValue(value);
    }
  }, []);

  const actions = useMemo(
    () => [
      {
        text: Strings.CANCEL,
        onClick: handleClose,
        className:
          'discount-modal__button discount-modal__button--secondary font-semibold',
        disabled: false,
      },
      {
        text: Strings.APPLY,
        onClick: handleApply,
        className: clsx(
          'discount-modal__button font-semibold',
          'discount-modal__button--primary'
        ),
        disabled:
          Boolean(error) || !discountValue || Number(discountValue) <= 0,
      },
    ],
    [handleClose, handleApply, error, discountValue]
  );

  const estimate = useMemo(() => {
    const basePrice =
      orderDiscountType === 'item' ? data?.price || 0 : orderTotalPrice;

    return [
      {
        text: Strings.OLD_PRICE,
        value: getFormattedAmount(basePrice),
        highlight: false,
      },
      {
        text: `${
          discountType === 'percent' ? '%' : getCurrencySign()
        } Discount`,
        value: discountValue
          ? `- ${getFormattedAmount(discountAmountInMinor)}`
          : `- ${getCurrencySign()} 0.00`,
        highlight: true,
        divider: true,
      },
      {
        text: Strings.NEW_PRICE,
        value: getFormattedAmount(newPrice),
        highlight: false,
      },
      {
        text: Strings.QUANTITY,
        value: `x ${data?.newQty || 0}`,
        highlight: false,
        divider: true,
        level: 'item',
      },
      {
        text: Strings.INLINE_PRICE,
        value: getFormattedAmount(newPrice * (data?.newQty || 0)),
        level: 'item',
        highlight: false,
      },
    ];
  }, [
    orderDiscountType,
    data,
    orderTotalPrice,
    discountType,
    discountValue,
    discountAmountInMinor,
    newPrice,
  ]);

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      disableSwipeToOpen={true}
      PaperProps={{ className: 'discount-modal__drawer-paper' }}
    >
      <Box className="discount-modal__container">
        {isOrderSummary ? (
          <div className="discount-modal-order-summary__view">
            <div onClick={onClose} className="order-summary-left font-semibold">
              {'Close'}
            </div>
            <div className="order-summary-center font-semibold">
              {'Order Summary'}
            </div>
          </div>
        ) : (
          <div className="discount-modal__header font-semibold">
            {orderDiscountType === 'item'
              ? Strings.ADD_ITEM_LEVEL_DISC
              : Strings.ADD_ORDER_LEVEL_DISC}
          </div>
        )}

        {isOrderSummary ? (
          <div className="discount-modal__summary">
            <OrderLedgerSummary
              orderSummary={orderSummary}
              showDivider={false}
              needHeading={false}
            />
          </div>
        ) : (
          <div>
            <div className="discount-modal__summary discount-modal__with-padding-bottom">
              {orderDiscountType === 'item' ? (
                <ItemLevelSummary data={data} />
              ) : (
                <OrderLedgerSummary orderSummary={orderSummary} />
              )}
              <div className="discount-modal__input-section">
                <div className="discount-modal__input-label font-medium">
                  <span>
                    {discountType === 'percent'
                      ? Strings.DISCOUNT_INFO_LABEL_PERCENT
                      : Strings.DISCOUNT_INFO_LABEL_VALUE}
                  </span>
                </div>
                <div
                  className="discount-modal__input-row"
                  style={{
                    border: `1px solid ${error ? '#e53935' : '#758195'}`,
                  }}
                >
                  <div className="discount-modal__toggle-group">
                    <button
                      className={clsx(
                        'discount-modal__toggle-button',
                        discountType === 'direct' &&
                          'discount-modal__toggle-button--selected'
                      )}
                      onClick={() => handleDiscountTypeChange('direct')}
                      type="button"
                    >
                      {getCurrencySign()}
                    </button>
                    <button
                      className={clsx(
                        'discount-modal__toggle-button',
                        discountType === 'percent' &&
                          'discount-modal__toggle-button--selected'
                      )}
                      onClick={() => handleDiscountTypeChange('percent')}
                      type="button"
                    >
                      {Strings.PERCENT}
                    </button>
                  </div>
                  <input
                    className="discount-modal__input font-medium"
                    placeholder={Strings.ENTER_VALUE}
                    type="number"
                    inputMode="decimal"
                    value={discountValue}
                    onChange={(e) => handleDiscountValueChange(e.target.value)}
                    min={0}
                  />
                </div>
                {error && (
                  <div className="discount-modal__error font-medium">
                    {error}
                  </div>
                )}
              </div>

              <div className="discount-modal__estimate">
                <div className="discount-modal__estimate-label font-semibold">
                  {Strings.ESTIMATE}
                </div>
                {estimate
                  .filter((_) =>
                    orderDiscountType === 'order' ? _.level !== 'item' : true
                  )
                  .map((item, idx) => (
                    <React.Fragment key={idx}>
                      <div
                        className={clsx(
                          'discount-modal__estimate-row',
                          item.highlight
                            ? 'discount-modal__estimate-row--highlight font-medium'
                            : 'discount-modal__estimate-row--no-highlight font-regular'
                        )}
                      >
                        {item.text}
                        <span
                          className={
                            item.highlight
                              ? 'discount-modal__estimate-row--highlight font-medium'
                              : 'discount-modal__estimate-row--value'
                          }
                        >
                          {item.value}
                        </span>
                      </div>
                      {item.divider && (
                        <div className="discount-modal__estimate-row--divider" />
                      )}
                    </React.Fragment>
                  ))}
              </div>
            </div>
            <div className="discount-modal__actions">
              {actions.map((button) => (
                <button
                  key={button.text}
                  className={button.className}
                  onClick={button.onClick}
                  disabled={button.disabled}
                  type="button"
                >
                  {button.text}
                </button>
              ))}
            </div>
          </div>
        )}
      </Box>
    </SwipeableDrawer>
  );
}
